"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { SearchFilters } from "@/lib/api/types";

export const TALENT_SEARCH_PER_PAGE = 15;
const EMPTY_FILTERS: SearchFilters = {};
const noop = () => {};

export interface TalentSearchFiltersContextValue {
  filters: SearchFilters;
  page: number;
  perPage: number;
  openSections: Record<string, boolean>;
  setFilters: (filters: SearchFilters) => void;
  updateFilter: <K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K],
  ) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  toggleSection: (key: string) => void;
  resetFilters: () => void;
  hydrateFilters: (filters: SearchFilters) => void;
  /**
   * Talent pools the search covers. Only a search session (the draft-backed
   * provider) has pools; elsewhere (hero, presets) both are absent and the
   * pool picker and category gating stay off.
   */
  poolIds?: string[] | null;
  setPoolIds?: (poolIds: string[]) => void;
}

const TalentSearchFiltersContext =
  createContext<TalentSearchFiltersContextValue | null>(null);

export function TalentSearchFiltersProvider({
  children,
  initialFilters = EMPTY_FILTERS,
  onFiltersChange,
  resetKey,
}: {
  children: React.ReactNode;
  initialFilters?: SearchFilters;
  onFiltersChange?: (filters: SearchFilters) => void;
  resetKey?: unknown;
}) {
  const initialFiltersRef = useRef(initialFilters);
  const [filters, setFiltersState] = useState<SearchFilters>(initialFilters);
  const [page, setPageState] = useState(1);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  useEffect(() => {
    setFiltersState(initialFiltersRef.current);
    setPageState(1);
    setOpenSections({});
  }, [resetKey]);

  const setFilters = useCallback((nextFilters: SearchFilters) => {
    setFiltersState(nextFilters);
    setPageState(1);
    onFiltersChange?.(nextFilters);
  }, [onFiltersChange]);

  const updateFilter = useCallback(
    <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
      setFiltersState((current) => {
        const nextFilters = { ...current, [key]: value };
        onFiltersChange?.(nextFilters);
        return nextFilters;
      });
      setPageState(1);
    },
    [onFiltersChange],
  );

  const clearFilters = useCallback(() => {
    setFiltersState({});
    setPageState(1);
    onFiltersChange?.({});
  }, [onFiltersChange]);

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage);
  }, []);

  const toggleSection = useCallback((key: string) => {
    setOpenSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState({});
    setPageState(1);
    setOpenSections({});
    onFiltersChange?.({});
  }, [onFiltersChange]);

  const hydrateFilters = useCallback((nextFilters: SearchFilters) => {
    setFiltersState(nextFilters);
    setPageState(1);
    onFiltersChange?.(nextFilters);
  }, [onFiltersChange]);

  const value = useMemo<TalentSearchFiltersContextValue>(
    () => ({
      filters,
      page,
      perPage: TALENT_SEARCH_PER_PAGE,
      openSections,
      setFilters,
      updateFilter,
      clearFilters,
      setPage,
      toggleSection,
      resetFilters,
      hydrateFilters,
    }),
    [
      clearFilters,
      filters,
      hydrateFilters,
      openSections,
      page,
      resetFilters,
      setFilters,
      setPage,
      toggleSection,
      updateFilter,
    ],
  );

  return (
    <TalentSearchFiltersContext.Provider value={value}>
      {children}
    </TalentSearchFiltersContext.Provider>
  );
}

/**
 * Draft-backed adapter mounted by `TalentSearchResultsProvider` around a
 * search session. Inside it, `useTalentSearchFilters()` reads and writes the
 * search's unsaved definition draft (filters live there; Save is Run) and
 * paginates the run's results, so the filter inputs, assistant and results
 * table keep working unchanged. Outside it (the hero, project switcher) the
 * dashboard-level `TalentSearchFiltersProvider` keeps its own local state,
 * and a nested `TalentSearchFiltersProvider` (preset drafts) still wins for
 * its own subtree because it is the nearer context.
 *
 * Purely visual state (open sections) is shared with the parent provider
 * when one exists so it survives hero <-> session moves.
 * `hydrateFilters` is a no-op: the draft seeds itself from the server.
 */
export function TalentSearchDraftFiltersProvider({
  filters,
  setFilters,
  updateFilter,
  poolIds,
  setPoolIds,
  page,
  setPage,
  perPage,
  children,
}: {
  filters: SearchFilters | undefined;
  setFilters: (filters: SearchFilters) => void;
  updateFilter: TalentSearchFiltersContextValue["updateFilter"];
  poolIds?: string[] | null;
  setPoolIds?: (poolIds: string[]) => void;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  children: React.ReactNode;
}) {
  const parent = useContext(TalentSearchFiltersContext);
  const [localOpenSections, setLocalOpenSections] = useState<
    Record<string, boolean>
  >({});
  const toggleLocalSection = useCallback((key: string) => {
    setLocalOpenSections((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  const openSections = parent?.openSections ?? localOpenSections;
  const toggleSection = parent?.toggleSection ?? toggleLocalSection;

  const clearFilters = useCallback(() => setFilters({}), [setFilters]);

  const value = useMemo<TalentSearchFiltersContextValue>(
    () => ({
      filters: filters ?? EMPTY_FILTERS,
      page,
      perPage,
      openSections,
      setFilters,
      updateFilter,
      clearFilters,
      setPage,
      toggleSection,
      resetFilters: clearFilters,
      hydrateFilters: noop,
      poolIds,
      setPoolIds,
    }),
    [
      clearFilters,
      filters,
      openSections,
      page,
      perPage,
      poolIds,
      setFilters,
      setPage,
      setPoolIds,
      toggleSection,
      updateFilter,
    ],
  );

  return (
    <TalentSearchFiltersContext.Provider value={value}>
      {children}
    </TalentSearchFiltersContext.Provider>
  );
}

export function useTalentSearchFilters() {
  const context = useContext(TalentSearchFiltersContext);
  if (!context) {
    throw new Error(
      "useTalentSearchFilters must be used within TalentSearchFiltersProvider",
    );
  }
  return context;
}

export function useOptionalTalentSearchFilters() {
  return useContext(TalentSearchFiltersContext);
}
